import {
  Button,
  Card,
  CardActionArea,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputBase,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  fade,
  makeStyles
} from '@material-ui/core/styles';
import {
  ClearRounded,
  CloseRounded,
  FilterListRounded,
  LaunchRounded,
  SearchRounded,
  SignalCellularAltRounded
} from '@material-ui/icons';
import { Autocomplete, Pagination } from '@material-ui/lab';
import { DatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import clsx from 'clsx';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RRLink } from 'react-router-dom';
import { RootState } from '../../app/store';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import {
  BusinessUnit,
  getBusinessUnitsAsync
} from '../BusinessUnit/businessUnitSlice';
import { getOverviewProjectsAsync } from './dashboardSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    titleBox: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        display: 'block',
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
      borderRadius: theme.spacing(2)
    },
    divider: {
      margin: theme.spacing(0.5, 0),
      width: theme.spacing(4)
    },
    chip: {
      margin: theme.spacing(0, -0.5),
      cursor: 'pointer',
      fontWeight: 'normal',
      fontSize: 11,
      height: 20
    },
    filterDashboardContainer: {
      padding: theme.spacing(1, 2, 1, 1),
      borderRadius: theme.spacing(2),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    marginFilterButton: {
      marginLeft: theme.spacing(1)
    },
    statusContainer: {
      display: 'flex',
      '& > *': {
        width: '33.33%',
        marginRight: theme.spacing(0.25)
      }
    },
    statusCard: {
      border: '2px solid #cccccc55',
      borderRadius: theme.spacing(2, 2, 0, 0),
      transition: '0.4s',
      cursor: 'pointer',
      '&:last-child': {
        marginRight: 0
      }
    },
    statusCardAction: {
      padding: theme.spacing(1, 2),
      display: 'flex',
      justifyContent: 'space-between',
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.5, 1, 0, 1),
        display: 'block',
        textAlign: 'center'
      }
    },
    statusLabel: {
      wordBreak: 'break-all',
      [theme.breakpoints.down('xs')]: {
        fontSize: 12,
        lineHeight: '100%'
      }
    },
    paperSearch: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 2, 1, 1),
      borderRadius: 18,
      width: 240,
      height: 36,
      zIndex: 2
    },
    searchInput: {
      marginLeft: theme.spacing(1)
    },
    colorTextSecondary: {
      color: theme.palette.text.secondary
    },
    projectCard: {
      display: 'flex',
      alignItems: 'center',
      borderBottom: '2px solid #cccccc55',
      padding: theme.spacing(1, 0),
      '&:last-child': {
        borderBottom: 'none'
      },
      textDecoration: 'none',
      color: theme.palette.text.primary,
      transition: '0.4s',
      '&:hover, &.Mui-focusVisible': {
        color: theme.palette.primary.main
      },
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    progressBox: {
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'flex-end',
        marginTop: theme.spacing(1)
      }
    },
    progressField: {
      width: 80,
      padding: theme.spacing(0, 1),
      textAlign: 'center',
      '& > *': {
        lineHeight: '120%'
      }
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexGrow: {
      flexGrow: 1
    },
    marginLeft: {
      marginLeft: theme.spacing(1)
    },
    lineHeight120: {
      lineHeight: '120%'
    },
    filterContainer: {
      padding: theme.spacing(1, 2, 2, 2),
      '& > *': {
        marginTop: theme.spacing(1)
      }
    },
    filterBusinessUnit: {
      width: '100%',
      marginBottom: theme.spacing(1)
    },
    filterYear: {
      margin: theme.spacing(2, 0, 4, 0)
    },
    filterYearField: {
      width: 80,
      marginLeft: theme.spacing(1)
    },
    flexGrowName: {
      flexGrow: 1,
      padding: theme.spacing(0, 1),
      lineHeight: '120%'
    },
    filterChip: {
      margin: theme.spacing(0.5)
    },
    filterButton: {
      '& > *': {
        marginTop: theme.spacing(1)
      }
    },
    drawerDesktop: {
      display: 'block',
      [theme.breakpoints.down('xs')]: {
        display: 'none'
      },
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: 400,
        borderRadius: theme.spacing(2, 0, 0, 2)
      }
    },
    drawerMobile: {
      display: 'none',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      },
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: '100%',
        borderRadius: theme.spacing(2, 2, 0, 0)
      }
    },
    drawerHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0, 1, 0, 3),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar
    },
    fileNotFound: {
      padding: theme.spacing(4.3, 2),
      textAlign: 'center',
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(7.65, 2)
      }
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    selectedObject: {
      borderColor: theme.palette.primary.main,
      backgroundColor: fade(theme.palette.primary.main, 0.1)
    },
    selectedObjectGreen: {
      borderColor: theme.palette.success.main,
      backgroundColor: fade(theme.palette.success.main, 0.1)
    },
    selectedObjectRed: {
      borderColor: theme.palette.error.main,
      backgroundColor: fade(theme.palette.error.main, 0.1)
    },
    selectedObjectBlack: {
      borderColor: theme.palette.text.primary,
      backgroundColor: fade(theme.palette.text.primary, 0.1)
    },
    colorPrimary: {
      color: theme.palette.primary.main
    },
    colorGreen: {
      color: theme.palette.success.main
    },
    colorRed: {
      color: theme.palette.error.main
    },
    colorBlack: {
      color: theme.palette.text.primary
    }
  })
);

enum Status {
  ALL = 'all',
  OVERDUE = 'overdue',
  ON_TIME = 'onTime',
  CLOSED = 'closed'
}

interface StatusTab {
  status: Status;
  label: string;
  count: number;
  color: string;
  activeClass: string;
}

enum KeyChip {
  DATE = 'date',
  BUSINESS_UNIT = 'businessUnit'
}

interface FilterChip {
  key: KeyChip;
  label: string;
  value: number | BusinessUnit;
}

export default function Dashboard() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { data: businessUnits } = useSelector(
    (state: RootState) => state.businessUnits
  );
  const { projects } = useSelector((state: RootState) => state.dashboards);

  const [canGetChartData, setCanGetChartData] = useState<boolean>(false);
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<
    BusinessUnit[]
  >([]);
  const [selectedDate, setSelectedDate] = useState<{
    startDate: Date;
    endDate: Date;
  }>({ startDate: new Date(), endDate: new Date() });
  const [selectedYear, setSelectedYear] = useState<{
    startYear: number;
    endYear: number;
  }>({
    startYear: new Date().getFullYear(),
    endYear: new Date().getFullYear()
  });
  const [appliedFilter, setAppliedFilter] = useState<FilterChip[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<Status>(Status.ALL);
  const [search, setSearch] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const limit = 20;

  const [openFilter, setOpenFilter] = useState<boolean>(false);

  const Statuses: StatusTab[] = [
    {
      status: Status.ALL,
      label: t('dashboardIndex.statuses.all') as string,
      count: 10,
      color: classes.colorPrimary,
      activeClass: classes.selectedObject
    },
    {
      status: Status.OVERDUE,
      label: t('dashboardIndex.statuses.overdue') as string,
      count: 6,
      color: classes.colorRed,
      activeClass: classes.selectedObjectRed
    },
    {
      status: Status.ON_TIME,
      label: t('dashboardIndex.statuses.onTime') as string,
      count: 4,
      color: classes.colorGreen,
      activeClass: classes.selectedObjectGreen
    },
    {
      status: Status.CLOSED,
      label: t('dashboardIndex.statuses.closed') as string,
      count: 0,
      color: classes.colorBlack,
      activeClass: classes.selectedObjectBlack
    }
  ];

  const drawer = (
    <div role="presentation">
      <div className={classes.drawerHeader}>
        <Typography variant="h6">{t('dashboard.filter.title')}</Typography>
        <Tooltip title={t('common.close') as string}>
          <IconButton onClick={handleFilterDrawer(false)}>
            <CloseRounded />
          </IconButton>
        </Tooltip>
      </div>
      <Divider />
      <div className={classes.filterContainer}>
        <Autocomplete
          className={classes.filterBusinessUnit}
          id="business-unit"
          options={businessUnits}
          multiple
          getOptionLabel={(option) => option?.name}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                label={`${option?.name}`}
                {...getTagProps({ index })}
                variant="outlined"
                size="small"
                deleteIcon={<ClearRounded />}
              />
            ))
          }
          value={selectedBusinessUnits}
          getOptionSelected={(option, value) => option.id === value.id}
          onChange={(event, newValue) => {
            setSelectedBusinessUnits(newValue);
            setCanGetChartData(true);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={
                selectedBusinessUnits.length === 0
                  ? (t('dashboard.filter.filterByBusinessUnit') as string)
                  : ''
              }
            />
          )}
        />
        <div className={clsx(classes.flexAlignCenter, classes.filterYear)}>
          <Typography
            className={classes.flexGrowName}
            variant="body2"
            align="right"
          >
            {t('dashboard.filter.createdYear')}
          </Typography>
          <DatePicker
            className={classes.filterYearField}
            autoOk
            size="small"
            variant="inline"
            inputVariant="outlined"
            views={['year']}
            label={t('dashboard.filter.from')}
            value={selectedDate.startDate}
            onChange={(newValue) => {
              updateDatePicker(newValue, 'startDate');
            }}
            disableFuture
          />
          <DatePicker
            className={classes.filterYearField}
            autoOk
            size="small"
            variant="inline"
            inputVariant="outlined"
            views={['year']}
            label={t('dashboard.filter.to')}
            value={selectedDate.endDate}
            onChange={(newValue) => {
              updateDatePicker(newValue, 'endDate');
            }}
            disableFuture
          />
        </div>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={resetData}
        >
          {t('common.clear')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          disabled={!canGetChartData}
          onClick={requestNewData}
        >
          {t('common.apply')}
        </Button>
      </div>
    </div>
  );

  useEffect(() => {
    dispatch(getBusinessUnitsAsync());
    getOverviewProjects({
      businessUnits: [],
      date: { startDate: new Date(), endDate: new Date() },
      status: Status.ALL,
      search: null,
      page: 0,
      limit
    });
  }, []);

  function getDashboardData({
    businessUnits,
    date,
    status
  }: {
    businessUnits: BusinessUnit[];
    date: { startDate: Date; endDate: Date };
    status: Status;
  }): void {
    setOpenFilter(false);
    setCanGetChartData(false);
    const params = {
      businessUnitNames: businessUnits.map((businessUnit) => businessUnit.name),
      startDate: new Date(
        moment(date.startDate).startOf('year').toLocaleString()
      ),
      endDate: new Date(moment(date.endDate).endOf('year').toLocaleString()),
      status,
      today: new Date(moment().toLocaleString())
    };
    //TODO: connect to API with dispatch for getting status here
  }

  function getOverviewProjects({
    businessUnits,
    date,
    status,
    search,
    page,
    limit
  }: {
    businessUnits: BusinessUnit[];
    date: { startDate: Date; endDate: Date };
    status: Status;
    search: string | null;
    page: number;
    limit: number;
  }): void {
    const params = {
      businessUnitNames: businessUnits.map((businessUnit) => businessUnit.name),
      startDate: new Date(
        moment(date.startDate).startOf('year').toLocaleString()
      ),
      endDate: new Date(moment(date.endDate).endOf('year').toLocaleString()),
      status,
      search,
      page,
      limit,
      today: new Date(moment().toLocaleString())
    };
    dispatch(getOverviewProjectsAsync(params));
  }

  function resetData(): void {
    setSelectedBusinessUnits([]);
    setSelectedDate({ startDate: new Date(), endDate: new Date() });
    setSelectedYear({
      startYear: new Date().getFullYear(),
      endYear: new Date().getFullYear()
    });
    setAppliedFilter([]);
    setSelectedStatus(Status.ALL);
    setSearch(null);
    setPage(0);
    getDashboardData({
      businessUnits: [],
      date: { startDate: new Date(), endDate: new Date() },
      status: Status.ALL
    });
    getOverviewProjects({
      businessUnits: [],
      date: { startDate: new Date(), endDate: new Date() },
      status: Status.ALL,
      search: null,
      page: 0,
      limit
    });
  }

  function requestNewData(): void {
    function getAppliedFilter(): FilterChip[] {
      const businessUnitChips: FilterChip[] = selectedBusinessUnits.map(
        (businessUnit) => {
          return {
            key: KeyChip.BUSINESS_UNIT,
            label: businessUnit.name,
            value: businessUnit
          };
        }
      );
      return [...businessUnitChips];
    }
    setAppliedFilter(getAppliedFilter());
    setPage(0);
    setSelectedYear({
      startYear: selectedDate.startDate.getFullYear(),
      endYear: selectedDate.endDate.getFullYear()
    });
    getDashboardData({
      businessUnits: selectedBusinessUnits,
      date: selectedDate,
      status: selectedStatus
    });
    getOverviewProjects({
      businessUnits: selectedBusinessUnits,
      date: selectedDate,
      status: selectedStatus,
      search,
      page: 0,
      limit
    });
  }

  function handleFilterDrawer(
    open: boolean
  ): (event: React.KeyboardEvent | React.MouseEvent) => void {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setOpenFilter(open);
    };
  }

  function findMinMaxDate(dates: Date[], type: 'min' | 'max'): Date {
    let date = dates[0];
    for (let i = 0; i < dates.length; i++) {
      if (type === 'max') {
        if (date < dates[i]) date = dates[i];
      } else if (type === 'min') {
        if (date > dates[i]) date = dates[i];
      }
    }
    return date;
  }

  function updateDatePicker(
    date: MaterialUiPickersDate,
    field: 'startDate' | 'endDate'
  ): void {
    if (date) {
      const newDates = { ...selectedDate, [field]: date };
      const dates = [newDates.startDate, newDates.endDate];
      const startDate = findMinMaxDate(dates, 'min');
      const endDate = findMinMaxDate(dates, 'max');
      setSelectedDate({ startDate, endDate });
    }
    setCanGetChartData(true);
  }

  function onClickStatusCard(status: Status): void {
    if (status !== selectedStatus) {
      setSelectedStatus(status);
      setPage(0);
      getOverviewProjects({
        businessUnits: selectedBusinessUnits,
        date: selectedDate,
        status,
        search,
        page: 0,
        limit
      });
    }
  }

  //Change search and load new projects when clear search field
  function handleChangeSearch(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const keyword = event.target.value;
    setSearch(keyword === '' ? null : keyword);
    if (!keyword) {
      setPage(0);
      getOverviewProjects({
        businessUnits: selectedBusinessUnits,
        date: selectedDate,
        status: selectedStatus,
        search: null,
        page: 0,
        limit
      });
    }
  }

  //Load new projects when search has it's value
  function handleSearch(): void {
    if (search) {
      setPage(0);
      getOverviewProjects({
        businessUnits: selectedBusinessUnits,
        date: selectedDate,
        status: selectedStatus,
        search,
        page: 0,
        limit
      });
    }
  }

  //Load new projects when page was changed
  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    value: number
  ): void {
    if (page !== value - 1) {
      setPage(value - 1); //The first value of pagination is 1, not 0
      getOverviewProjects({
        businessUnits: selectedBusinessUnits,
        date: selectedDate,
        status: selectedStatus,
        search,
        page: value - 1,
        limit
      });
    }
  }

  function filterChips(): FilterChip[] {
    const chips: FilterChip[] = [
      {
        key: KeyChip.DATE,
        label: `${t('dashboard.filter.from')}: ${selectedYear.startYear}`,
        value: selectedYear.startYear
      },
      {
        key: KeyChip.DATE,
        label: `${t('dashboard.filter.to')}: ${selectedYear.endYear}`,
        value: selectedYear.endYear
      },
      ...appliedFilter
    ];
    return chips;
  }

  function handleDeleteChip(chipToDelete: FilterChip) {
    return function () {
      let newBusinessUnits = selectedBusinessUnits;
      if (chipToDelete.key === KeyChip.BUSINESS_UNIT) {
        newBusinessUnits = selectedBusinessUnits.filter(
          (businessUnit) => businessUnit.name !== chipToDelete.label
        );
        setSelectedBusinessUnits(newBusinessUnits);
      }
      const newAppliedFilter = appliedFilter.filter(
        (filter) => filter.label !== chipToDelete.label
      );
      setAppliedFilter(newAppliedFilter);
      setPage(0);
      getOverviewProjects({
        businessUnits: newBusinessUnits,
        date: selectedDate,
        status: selectedStatus,
        search: null,
        page: 0,
        limit
      });
    };
  }

  return (
    <div className={classes.root}>
      <Breadcrumb name="dashboard" />
      <div className={classes.titleBox}>
        <Typography component="p" variant="h4">
          {t('dashboard.title')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<SignalCellularAltRounded />}
          onClick={() => {
            window.open(
              process.env.REACT_APP_DOCUMENT_LIST_REPORT_WEB_URL,
              '_blank'
            );
          }}
        >
          {t('dashboard.viewDocumentLists')}
        </Button>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.filterDashboardContainer}>
            <div className={classes.flexGrow}>
              {filterChips().map((chip, index) => {
                return (
                  <Chip
                    key={index}
                    className={classes.filterChip}
                    size="small"
                    variant="outlined"
                    label={chip.label}
                    onClick={
                      chip.key === KeyChip.DATE
                        ? handleFilterDrawer(true)
                        : undefined
                    }
                    onDelete={
                      chip.key === KeyChip.DATE
                        ? undefined
                        : handleDeleteChip(chip)
                    }
                    deleteIcon={<ClearRounded />}
                  />
                );
              })}
            </div>
            <Tooltip title={t('common.filter') as string} arrow>
              <IconButton
                size="small"
                edge="end"
                onClick={handleFilterDrawer(true)}
              >
                <FilterListRounded />
              </IconButton>
            </Tooltip>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <div className={classes.statusContainer}>
            {false && //TODO: Status of each project will be implement later
              Statuses.map(({ status, label, count, color, activeClass }) => {
                return (
                  <Card
                    className={clsx(classes.statusCard, {
                      [activeClass]: status === selectedStatus
                    })}
                    variant="outlined"
                    key={label}
                  >
                    <CardActionArea
                      className={classes.statusCardAction}
                      onClick={() => onClickStatusCard(status)}
                    >
                      <Typography
                        className={classes.statusLabel}
                        color="textSecondary"
                      >
                        {label}
                      </Typography>
                      <Typography className={color} variant="h5">
                        {count}
                      </Typography>
                    </CardActionArea>
                  </Card>
                );
              })}
          </div>
          <Paper className={classes.card}>
            <div className={classes.flexAlignCenter}>
              <Typography
                className={clsx(classes.flexGrow, classes.lineHeight120)}
                variant="subtitle1"
                gutterBottom
              >
                {t('dashboard.projects')}
              </Typography>
              <Paper className={classes.paperSearch} variant="outlined">
                <SearchRounded className={classes.colorTextSecondary} />
                <InputBase
                  placeholder={t('common.search') as string}
                  className={classes.searchInput}
                  type="search"
                  spellCheck={false}
                  fullWidth
                  value={search === null ? '' : search}
                  onChange={handleChangeSearch}
                  onKeyPress={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  inputProps={{ 'aria-label': 'search' }}
                />
              </Paper>
            </div>
            <Divider className={classes.divider} />
            {projects.count > 0 ? (
              projects.data.map((project) => {
                const isDelay =
                  project.sumPlanManHours > project.sumActualManHours;
                const isZeroSumTotal = project.sumTotalManHours === 0;
                const calculatePlan = isZeroSumTotal
                  ? '-'
                  : Math.round(
                      (project.sumPlanManHours / project.sumTotalManHours) * 100
                    );
                const calculateActual = isZeroSumTotal
                  ? '-'
                  : Math.round(
                      (project.sumActualManHours / project.sumTotalManHours) *
                        100
                    );
                return (
                  <RRLink
                    className={classes.projectCard}
                    key={project.id}
                    to={`/projects/${project.id}/dashboards`}
                    target="_blank"
                  >
                    <div className={classes.flexGrow}>
                      <div className={classes.flexAlignCenter}>
                        <Typography variant="subtitle1">
                          {project.projectNo}
                        </Typography>
                        <LaunchRounded
                          className={classes.marginLeft}
                          color="action"
                          fontSize="small"
                        />
                      </div>
                      <Typography variant="body2" gutterBottom>
                        {project.name}
                      </Typography>
                      {project.businessUnitName && (
                        <Chip
                          className={classes.chip}
                          label={project.businessUnitName}
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </div>
                    <div
                      className={clsx(
                        classes.flexAlignCenter,
                        classes.progressBox
                      )}
                    >
                      <Tooltip
                        title={t('dashboardIndex.tooltipPlan', {
                          planHours: project.sumPlanManHours,
                          totalHours: project.sumTotalManHours
                        }) as string}
                        arrow
                      >
                        <div className={classes.progressField}>
                          <Typography color="textSecondary" variant="body2">
                            {t('dashboardIndex.planPercent')}
                          </Typography>
                          <Typography color="textPrimary" variant="h5">
                            {calculatePlan}
                          </Typography>
                        </div>
                      </Tooltip>
                      <Tooltip
                        title={t('dashboardIndex.tooltipActual', {
                          actualHours: project.sumActualManHours,
                          totalHours: project.sumTotalManHours
                        }) as string}
                        arrow
                      >
                        <div className={classes.progressField}>
                          <Typography color="textSecondary" variant="body2">
                            {t('dashboardIndex.actualPercent')}
                          </Typography>
                          <Typography
                            className={clsx(classes.colorGreen, {
                              [classes.colorRed]: isDelay
                            })}
                            variant="h5"
                          >
                            {calculateActual}
                          </Typography>
                        </div>
                      </Tooltip>
                    </div>
                  </RRLink>
                );
              })
            ) : (
              <div className={classes.fileNotFound}>{t('dashboard.projectNotFound')}</div>
            )}
          </Paper>
          <div className={classes.pagination}>
            <Pagination
              count={Math.ceil(projects.count / limit)}
              shape="rounded"
              page={page + 1}
              onChange={handleChangePage}
            />
          </div>
        </Grid>
      </Grid>
      <Drawer
        className={classes.drawerDesktop}
        anchor="right"
        open={openFilter}
        onClose={handleFilterDrawer(false)}
      >
        {drawer}
      </Drawer>
      <Drawer
        className={classes.drawerMobile}
        anchor="bottom"
        open={openFilter}
        onClose={handleFilterDrawer(false)}
      >
        {drawer}
      </Drawer>
    </div>
  );
}
